import {useEffect, useState, useRef} from 'react';
import usePayinSessionQuery from 'src/api/graphql/usePayinSessionQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {isMobile} from 'src/utils/utils';
import {isInAppBrowser} from 'src/v2/util/isInAppBrowser';
import {notifyInContextComplete} from 'src/v2/util/inContextCompletion';
import {urlRedirect} from '@stripe-internal/safe-links';
import {getStore} from 'src/v2/state/store';
import {getLogger} from 'src/utils/logger';

const POLL_INTERVAL_MS = 2000;

const logger = getLogger();

export const usePaymentPolling = (): {
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
} => {
  const store = getStore();
  const [isPolling, setIsPolling] = useState(false);
  const {clientSecret, sessionId} = useSessionContext();
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );
  const returnUrl = store.useSelector(
    (state) => state.paymentDetails.returnUrl,
  );

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const payinSessionData = usePayinSessionQuery({
    variables: {
      input: {
        crypto_payin_session: sessionId,
        client_secret: clientSecret,
      },
    },
    skip: !isPolling || !sessionId || !clientSecret,
    pollInterval: POLL_INTERVAL_MS,
    component: 'usePaymentPolling',
  });

  const isMobileDevice = isMobile();
  const inAppBrowser = isInAppBrowser(activeWallet, disabledWalletProviders);

  const autoRedirectDisabled = isMobileDevice && inAppBrowser;

  const stopPolling = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  };

  const startPolling = () => {
    setIsPolling(true);
  };

  // Each time payinSessionData is polled, check if the session is successful or failed or errored
  // If so, stop polling and redirect to the appropriate screen
  useEffect(() => {
    const payinSession = payinSessionData?.data?.v1CryptoInternalPayinSession;

    if (isPolling && payinSession) {
      const sessionState = payinSession.state;

      store.setValue((state) => {
        state.state = payinSession.state;
        state.transactionDetails.blockchainTxId =
          payinSession.transaction_details?.blockchain_tx_id ?? null;
        state.hasSucceededOrProcessingSetupAttempt =
          payinSession.has_succeeded_or_processing_setup_attempt;
      });

      if (sessionState === 'fulfillment_complete') {
        // If this is being rendered in-context, we render success screen
        // We can't automatically redirect on mobile devices, we render success screen instead
        if (notifyInContextComplete() || autoRedirectDisabled) {
          store.setValue((state) => {
            state.screen = 'SUCCESS';
          });
        } else {
          // We won't render success screen when redirect. Emit a log event to track the completion.
          logger.log('crypto_payin_session.screen.rendered', {
            screen: 'SUCCESS',
            message: 'redirect for payin session',
          });
          urlRedirect(returnUrl);
        }
      } else if (sessionState === 'error' || sessionState === 'canceled') {
        store.setValue((state) => {
          state.screen = sessionState === 'error' ? 'ERROR' : 'CANCELED';
        });
      }
    }
  }, [isPolling, payinSessionData, autoRedirectDisabled, store, returnUrl]);

  return {
    isPolling,
    startPolling,
    stopPolling,
  };
};
